import { AnimatePresence, motion } from 'framer-motion'
import PropTypes from 'prop-types'
import { useTheme, useCardTheme, useTextTheme, useBorderTheme, useButtonStyle } from '../../theme/hooks'

export type SaveChangesFooterProps = {
  isVisible: boolean
  onSave: () => void | Promise<void>
  onCancel: () => void | Promise<void>
  saving?: boolean
  cancelLabel?: string
  saveLabel?: string
  className?: string
}

const SaveChangesFooter = ({
  isVisible,
  onSave,
  onCancel,
  saving = false,
  cancelLabel = 'Annuler',
  saveLabel = 'Enregistrer les modifications',
  className = '',
}: SaveChangesFooterProps) => {
  const { theme } = useTheme()
  const cardTheme = useCardTheme()
  const textTheme = useTextTheme()
  const borderTheme = useBorderTheme()
  const primaryButtonStyle = useButtonStyle('primary')
  const secondaryButtonStyle = useButtonStyle('secondary')

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
          className={`fixed inset-x-0 bottom-0 z-[10000] flex items-center justify-center px-4 pb-4 sm:px-6 ${className}`}
        >
          <div
            className="flex w-full max-w-3xl items-center justify-between gap-3 rounded-xl border p-3 backdrop-blur"
            style={{
              borderColor: borderTheme.primary,
              backgroundColor: cardTheme.bg + 'f2', // slight transparency
              boxShadow: cardTheme.shadowElevated,
            }}
          >
            <div className="px-2 text-sm" style={{ color: textTheme.secondary }}>
              Des modifications non enregistrées sont en cours
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onCancel}
                disabled={saving}
                className="rounded-lg border px-4 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                style={{
                  ...secondaryButtonStyle,
                  borderColor: borderTheme.primary,
                }}
                onMouseEnter={(e) => {
                  if (!saving) e.currentTarget.style.backgroundColor = theme.button.secondary.bgHover
                }}
                onMouseLeave={(e) => {
                  if (!saving) e.currentTarget.style.backgroundColor = secondaryButtonStyle.backgroundColor
                }}
              >
                {cancelLabel}
              </button>
              <button
                type="button"
                onClick={onSave}
                disabled={saving}
                className="rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                style={saving ? theme.button.disabled : primaryButtonStyle}
                onMouseEnter={(e) => {
                  if (!saving) e.currentTarget.style.backgroundColor = theme.button.primary.bgHover
                }}
                onMouseLeave={(e) => {
                  if (!saving) e.currentTarget.style.backgroundColor = primaryButtonStyle.backgroundColor
                }}
              >
                {saving ? 'Enregistrement…' : saveLabel}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

SaveChangesFooter.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  cancelLabel: PropTypes.string,
  saveLabel: PropTypes.string,
  className: PropTypes.string,
}

export default SaveChangesFooter
